import { trackEvent } from './analytics';

// Thin client for the Global Cart Kit backend (backend-for-frontend / API proxy).
// All third-party calls go through this base URL; no API keys live in the browser.
// Configure via VITE_API_BASE_URL in your .env file.

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000'
).replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function errorTypeFor(status: number): string {
  if (status === 0) return 'network';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limit';
  if (status >= 500) return 'server';
  return 'client';
}

// One choke point for every tool's API failures. `path` is a fixed endpoint
// string -- query parameters, which can carry user input, are never included.
function reportApiError(path: string, status: number): void {
  trackEvent('api_error', {
    error_type: errorTypeFor(status),
    source: path,
    result_count: status,
  });
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  // Base argument is required for a relative API_BASE_URL (same-origin
  // production deploys); it's ignored by URL() whenever the first argument
  // is already absolute (e.g. local dev's http://localhost:4000).
  const url = new URL(`${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | undefined>
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    reportApiError(path, 0);
    throw new ApiError(0, 'Unable to reach the API server.');
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // non-JSON body; leave as null
  }

  if (!response.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : undefined) || `Request failed (${response.status})`;
    reportApiError(path, response.status);
    throw new ApiError(response.status, message);
  }

  return body as T;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    reportApiError(path, 0);
    throw new ApiError(0, 'Unable to reach the API server.');
  }

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    // non-JSON body; leave as null
  }

  if (!response.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : undefined) || `Request failed (${response.status})`;
    reportApiError(path, response.status);
    throw new ApiError(response.status, message);
  }

  return parsed as T;
}

export const apiBaseUrl = API_BASE_URL;
